<script lang="ts" setup>
    import '../../../css/choice.css';  
    import _ from "lodash";
    import {onMounted,onBeforeMount,computed,ref,watch,reactive} from 'vue';  
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import CodeValidationSetup from './CodeValidationSetup.vue';  
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import {ActionButtonStyle} from '../../lib/Types/Types';   
    import {valdiationRule,ErrorReturnMessage,FeesRetrieve,IValidationCopyResponse,IFeeItem,INegotiatedIDList} from '../../lib/Models/CodeValidationCopyModel';
    import {popAlert,ModalStyles} from '../../lib/Notification';     
    import {AlignmentType,ButtonDisplaySize,ButtonDisplayStyle,HoverCursorTypes,IconTypes,DisplayStyle} from "../../Enums/UiEnums"; 
    import {initPinia} from '../../lib/PiniaMgr';
    import {useCodeValidationCopyStore} from '../../lib/Stores/CodeValidationCopyStore';
    import {ICol} from "../../lib/ChoiceTableHelpers";
   
    import Icon from '../../components/Icon.vue'
    const selectedTable = ref<InstanceType<typeof ChoiceTable>>();      
    const selectedDisplayColumns = ref<ICol[]>([
      { Name: 'Code', DataField: 'procedureCode'},
      { Name: 'Start', DataField: 'feeStartDate', Alignment: AlignmentType.Center},
      { Name: 'End', DataField: 'feeEndDate', Alignment: AlignmentType.Center},
      { Name: 'Provider Name', DataField: 'providerName'},
      { Name: 'Financial Class', DataField: 'finClassName'},
      { Name: 'Payer Name', DataField: 'payerName'},
      { Name: 'Service Location', DataField: 'serviceLocationName'},
      { Name: 'Action', DataField: 'empty', Alignment: AlignmentType.Center },
    ]);
    
    const codeValidationCopyStore = useCodeValidationCopyStore(initPinia()); 

    const formData = reactive <
    {   
      validationList : number[],
      window: { windowPadding: number, footerHeight: number, searchHeight: number, resultHeight: number, workareaHeight: number},
      newSelectedItems? :  string[],
      oldSelectedItems? :  string[],
      newDisplayedItems?: string[],
      removedItems?: string[],
      displayedItems: string[],
      displayedRecords?: IFeeItem[]
    }>({    
      validationList: [0,1,2,3,4,5,6,7,8,9,10,11],
      window: { windowPadding: 1.5, footerHeight: 4.75, searchHeight: 8, resultHeight: 2, workareaHeight: 10},
      newSelectedItems: [],
      oldSelectedItems: [],
      newDisplayedItems: [],
      removedItems: [],
      displayedItems: []
    });
    const props = withDefaults(defineProps<{ 
        saveEnabled?: number,
        negotiatedId: number,
        codeValidationResults: IValidationCopyResponse, 
        selectedItems?: string
    }>(),
    {
        negotiatedId: 0,
        selectedItems: ""
    });
    onBeforeMount(async () => {
        codeValidationCopyStore.loadResults(props.codeValidationResults.ruleList);
        codeValidationCopyStore.setDisabled(valdiationRule.MinUnits,props.codeValidationResults.codeRecord.minUnitsDisabled);
        codeValidationCopyStore.setDisabled(valdiationRule.MaxUnits,props.codeValidationResults.codeRecord.maxUnitsDisabled);
        codeValidationCopyStore.setDisabled(valdiationRule.RenderingReferring,props.codeValidationResults.codeRecord.providerDisabled);
        codeValidationCopyStore.setProcedureCode(props.codeValidationResults.codeRecord.procedureCode);
    });
    onMounted(async () => {
        resetHeights();
        window.addEventListener('resize', () => {resetHeights();});  
    });  
    watch(() => props.selectedItems, (newValue : string, oldValue: string)  => { 
        if (newValue == "*") {
          formData.oldSelectedItems = [];
          formData.newSelectedItems = [];
          formData.removedItems = [];
          formData.newDisplayedItems = [];
          return;
        }
        if (newValue !== oldValue) { 
          formData.oldSelectedItems = oldValue.toString().split("~").filter(item => item != "");
          formData.newSelectedItems = newValue.toString().split("~").filter(item => item != "");
          formData.removedItems = _.difference(formData.oldSelectedItems, formData.newSelectedItems);
          formData.newDisplayedItems =  _.difference(formData.newSelectedItems, formData.displayedItems);
          removeDisplayedItems();
          getNewItems();
        }
    });
    const saveButtonDisabled = computed<boolean>(()=> {
      if ((codeValidationCopyStore.codeValidationResults.filter(item => item.ruleId != valdiationRule.RenderingReferring && item.ruleValue == "").length == 10) &&
          (codeValidationCopyStore.codeValidationResults.filter(item => item.ruleId == valdiationRule.RenderingReferring && item.ruleValue == "0").length == 1) ){ 
        return true;
      }
      return ((formData.displayedRecords?.length ?? 0) == 0 ? true : false);
    }); 
    function removeDisplayedItems() : void {
      if (!formData.removedItems) {
        return;
      }  
      if (formData.removedItems.length == 0) {
        return;
      }  
      _.pullAll(formData.displayedItems,formData.removedItems);
      if (!formData.displayedRecords) {
        return;
      }
      formData.removedItems.forEach(element => {
        formData.displayedRecords = formData.displayedRecords?.filter(item => item.negotiatedId != +element);        
      }); 
    }   
    async function getNewItems() : Promise<void> { 
      if (!formData.newDisplayedItems) {
        return;
      }
      if (formData.newDisplayedItems.length == 0) {
        return;
      }
      const messageTitle = "Add Fee"
      const list : INegotiatedIDList[] = formData.newDisplayedItems.map(item => ({negotiatedId: +item})); 
      const returnResults = await FeesRetrieve({negotiatedFeeList: list});
      if (!returnResults.success) {  
        popAlert(returnResults.error?.error ?? ErrorReturnMessage.feesReturn,messageTitle,ModalStyles.danger);
        return;
      }
      const newItems = returnResults.success.feeRecords.map(item => item.negotiatedId.toString());
      if (newItems.length == 0) {
        popAlert(ErrorReturnMessage.noActiveFees,messageTitle,ModalStyles.danger);
        return;
      }
      formData.displayedItems = _.concat(formData.displayedItems, newItems);
      if (!formData.displayedRecords) {
        formData.displayedRecords = returnResults.success.feeRecords
      }
      else {
        returnResults.success.feeRecords.forEach(element => {
          if (formData.displayedRecords) { // Cause typescript doesn't believe in line 98?
            formData.displayedRecords.push(element);
          }
        }); 
      }
    }
    function resetHeights() : void {
        const windowHeight = window.innerHeight / 16;
        formData.window.workareaHeight = windowHeight - formData.window.windowPadding - formData.window.footerHeight; 
        formData.window.searchHeight = formData.window.workareaHeight * .7;
        formData.window.resultHeight = formData.window.workareaHeight - formData.window.searchHeight;
    }
    async function saveCodeValidations() : Promise<void> {
      const saveCompleted = await codeValidationCopyStore.saveCodeValidation(formData.displayedItems);
      if (saveCompleted) {
        window.close();
      }
    }
    async function closeCodeValidations() : Promise<void> {
      window.close();
    }
    function onDeletefee(row : IFeeItem) : void {
      _.pullAll(formData.displayedItems,[row.negotiatedId.toString()]);
      formData.displayedRecords = formData.displayedRecords?.filter(item => item.negotiatedId != row.negotiatedId);  
    }
</script> 

<style scoped> 
    .data-cell {
      FONT-SIZE: .75rem;
      font-family: Arial, sans-serif, Helvetica;
      padding: 2px 2px 2px 2px;
      cursor: default;
      vertical-align: top;
    }
</style>
<template>
    <div class="row">
      <div class="col-4">
        <table>
          <tr>
            <td class="fw-bold text-isalus-primary label-isalus-small pb-2" colspan="4">Copy from Procedure</td>
          </tr>
          <tr>
            <td class="text-isalus-primary label-isalus-small data-cell">Type:</td>
            <td class="data-cell">{{ props.codeValidationResults.codeRecord.codeType }}</td>
            <td class="text-isalus-primary label-isalus-small data-cell">Effective:</td>
            <td colspan="3" class="data-cell" >
              {{ (props.codeValidationResults.codeRecord.feeStartDate ?? "") }}
              {{ (props.codeValidationResults.codeRecord.feeEndDate ?? "") }}
            </td>
          </tr>
          <tr>
            <td class="text-isalus-primary label-isalus-small data-cell">Code:</td>
            <td class="data-cell">{{ props.codeValidationResults.codeRecord.procedureCode }}</td>
            <td class="text-isalus-primary label-isalus-small data-cell">Billing:</td>
            <td class="data-cell">{{ ( props.codeValidationResults.codeRecord.billCode ?? "") }}</td>
          </tr>
          <tr>
            <td class="text-isalus-primary label-isalus-small data-cell">Description:</td>
            <td colspan="3" class="data-cell">{{ props.codeValidationResults.codeRecord.codeDescription }}</td>
          </tr>
          <tr>
            <td class="text-isalus-primary label-isalus-small data-cell">Provider:</td>
            <td class="data-cell" colspan="3">{{ (props.codeValidationResults.codeRecord.providerName ?? "") }}</td>
          </tr>
          <tr>
            <td class="text-isalus-primary label-isalus-small data-cell">Fin. Class:</td>
            <td class="data-cell" colspan="3">{{ (props.codeValidationResults.codeRecord.finClassName ?? "") }}</td>
          </tr>
          <tr>
            <td class="text-isalus-primary label-isalus-small data-cell">Payer:</td>
            <td class="data-cell" colspan="3">{{ (props.codeValidationResults.codeRecord.payerName ?? "") }}</td>
          </tr>
          <tr>
            <td class="text-isalus-primary label-isalus-small data-cell">Location:</td>
            <td class="data-cell" colspan="3">{{ (props.codeValidationResults.codeRecord.serviceLocationName ?? "") }}</td>
          </tr>
        </table>
        <hr class=" text-isalus-primary "/>
        <div class="fw-bold text-isalus-primary label-isalus-small pb-2" colspan="4">Code Validations</div>
        <div v-for="item in formData.validationList" > 
          <CodeValidationSetup
            :saveEnabled="props.saveEnabled"
            :validationID="item"
          />
        </div>
      </div>
      <div class="col-8">
        <iframe src="/officemd/screens/tablesearch.htm?SPV=Y&ID=19" scrolling="no" class="border-isalus-primary" :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'width': '96%', 'height': (formData.window.searchHeight) + 'rem'}"></iframe>
        <div :style="{'width': '96%',  'height': (formData.window.resultHeight) + 'rem'}">
          <div class="fw-bold text-isalus-primary label-isalus-small pb-2" colspan="4">Selected Procedures</div>
          <div class= "label-isalus-small skinnyscroll border-isalus-primary" :style="{'overflow': 'auto', 'width': '100%', 'height': (formData.window.resultHeight - 2) + 'rem'}">
            <ChoiceTable 
                ref="selectedTable"
                :columns="selectedDisplayColumns" 
                :data="formData.displayedRecords" 
                :show-filters="false" 
                :allowSorting="false"
                :key-value-data-member="'negotiatedId'">
                <template #cell="{col, row, value}">
                  <template v-if="'Action' === col.Name">
                      <Icon :icon="IconTypes.Trashcan" :displayStyle="DisplayStyle.Danger" :hoverCursor="HoverCursorTypes.Pointer" title="Remove this item." @click="onDeletefee(row as IFeeItem)"/>
                  </template>
                </template>
            </ChoiceTable> 
          </div>
        </div>
      </div>
    </div>
    <div class="row">
        <div class="col label-isalus-small fw-bold text-isalus-primary text-end pt-2 " :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'height': (formData.window.footerHeight) + 'rem'}">
          <hr/>
          <span class="pe-2">
            <ActionButtonV2
              :action="ActionButtonStyle.Save"
              :disabled="saveButtonDisabled"
              :text="'Save'"
              :tooltip="'Save Code Validations'"
              :size="ButtonDisplaySize.Small" 
              :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
              :performAction="saveCodeValidations"/>
          </span>          
          <span class="pe-2">
            <ActionButtonV2
                :action="ActionButtonStyle.Close"
                :disabled="false"
                :text="'Close'"
                :tooltip="'Close Code Validation Copy'"
                :size="ButtonDisplaySize.Small" 
                :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                :performAction="closeCodeValidations"/>
          </span>         
        </div>
    </div> 
</template>
